/**
 * Service Status Monitor Component
 * Displays real-time service health and degradation status
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import Badge from './ui/Badge';
import Button from './ui/Button';
import { AlertTriangle, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { optimizedApiService } from '../lib/apiOptimized';
import { errorHandler } from '../lib/errorHandler';

interface ServiceStatus {
  available: boolean;
  degradation_level: 'full_service' | 'limited_service' | 'emergency_mode' | 'offline';
  error_count: number;
  last_check: number;
  fallback_available: boolean;
}

interface ServicesStatus {
  [serviceName: string]: ServiceStatus;
}

const ServiceStatusMonitor: React.FC = () => {
  const [servicesStatus, setServicesStatus] = useState<ServicesStatus>({});
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchServiceStatus = async () => {
    try {
      setIsLoading(true);
      const response = await optimizedApiService.health.servicesStatus() as any;
      setServicesStatus(response.services || {});
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch service status:', error);
      errorHandler.handleApiError(error as any, 'Service Status');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceStatus();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchServiceStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: ServiceStatus) => {
    if (!status.available) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    
    switch (status.degradation_level) {
      case 'full_service':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'limited_service':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'emergency_mode':
      case 'offline':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: ServiceStatus) => {
    if (!status.available) {
      return <Badge variant="error">Offline</Badge>;
    }
    
    switch (status.degradation_level) {
      case 'full_service':
        return <Badge variant="success">Healthy</Badge>;
      case 'limited_service':
        return <Badge variant="warning">Limited</Badge>;
      case 'emergency_mode':
        return <Badge variant="error">Emergency</Badge>;
      case 'offline':
        return <Badge variant="error">Offline</Badge>;
      default:
        return <Badge variant="default">Unknown</Badge>;
    }
  };

  const getServiceDisplayName = (serviceName: string) => {
    const displayNames: { [key: string]: string } = {
      'ups_address_validation': 'Address Validation',
      'ups_rate_calculation': 'Rate Calculation',
      'ups_label_creation': 'Label Creation',
      'ups_tracking': 'Tracking'
    };
    
    return displayNames[serviceName] || serviceName;
  };

  const formatLastCheck = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else {
      return date.toLocaleTimeString();
    }
  };

  const overallHealth = Object.values(servicesStatus).every(status => 
    status.available && status.degradation_level === 'full_service'
  ) ? 'healthy' : 'degraded';

  const hasIssues = Object.values(servicesStatus).some(status => 
    !status.available || status.degradation_level !== 'full_service'
  );

  if (!isExpanded && !hasIssues) {
    return null; // Don't show if everything is healthy and collapsed
  }

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CardTitle className="text-lg">Service Status</CardTitle>
            {overallHealth === 'healthy' ? (
              <CheckCircle className="h-5 w-5 text-green-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchServiceStatus}
              loading={isLoading}
              className="flex items-center space-x-1"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        </div>
        {lastUpdate && (
          <p className="text-sm text-gray-600">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </p>
        )}
      </CardHeader>
      
      {isExpanded && (
        <CardContent>
          <div className="space-y-4">
            {Object.entries(servicesStatus).map(([serviceName, status]) => (
              <div
                key={serviceName}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getStatusIcon(status)}
                  <div>
                    <p className="font-medium text-gray-900">
                      {getServiceDisplayName(serviceName)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Last check: {formatLastCheck(status.last_check)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {getStatusBadge(status)}
                  
                  {status.error_count > 0 && (
                    <div className="text-sm text-gray-600">
                      {status.error_count} errors
                    </div>
                  )}
                  
                  {status.fallback_available && (
                    <Badge variant="default" className="text-xs">
                      Fallback Available
                    </Badge>
                  )}
                </div>
              </div>
            ))}
            
            {Object.keys(servicesStatus).length === 0 && !isLoading && (
              <div className="text-center py-8 text-gray-500">
                No service status information available
              </div>
            )}
          </div>
        </CardContent>
      )}
      
      {!isExpanded && hasIssues && (
        <CardContent className="pt-0">
          <div className="flex items-center space-x-2 text-sm text-yellow-600">
            <AlertTriangle className="h-4 w-4" />
            <span>Some services are experiencing issues. Click expand to view details.</span>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default ServiceStatusMonitor;
